import { useState, useEffect } from "react";
import { Plus, X, Search, Package, LocateFixed } from "lucide-react";
import { Modal, FormField, Input, Button } from "../ui/index";
import { cascadeAPI, noeudsAPI, membresAPI } from "../../api/services";
import MemberMultiSelect from "../ui/MemberMultiSelect";
import { useAuth } from "../../context/AuthContext";

export default function EditChantierModal({ isOpen, onClose, onEdit, item }) {
  const { user, canDo } = useAuth();
  const isRevendeur = user?.role === 'revendeur';

  const [form, setForm] = useState({
    nom: "", revendeur: "", client: "", projet: "",
    dateDebut: "", dateFin: "", etat: "En attente", commentaires: "",
  });
  const [chefChantier, setChefChantier] = useState([]);
  const [techniciens, setTechniciens] = useState([]);
  const [selectedNoeuds, setSelectedNoeuds] = useState([]);
  const [searchNoeud, setSearchNoeud] = useState("");
  const [filterCategorie, setFilterCategorie] = useState("");

  // Données dynamiques
  const [revendeurs, setRevendeurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projets, setProjets] = useState([]);
  const [membres, setMembres] = useState([]);
  const [noeudsDispos, setNoeudsDispos] = useState([]);

  // Prefill form when item changes
  useEffect(() => {
    if (!item || !isOpen) return;

    const revendeurId = typeof item.revendeur === "object" ? item.revendeur?._id : item.revendeur || "";
    const clientId = typeof item.client === "object" ? item.client?._id : item.client || "";
    const projetId = typeof item.projet === "object" ? item.projet?._id : item.projet || "";

    const formatDate = (d) => {
      if (!d) return "";
      return new Date(d).toISOString().split("T")[0];
    };

    setForm({
      nom: item.nom || "",
      revendeur: revendeurId,
      client: clientId,
      projet: projetId,
      dateDebut: formatDate(item.dateDebut),
      dateFin: formatDate(item.dateFin),
      etat: item.etat || "En attente",
      commentaires: item.commentaires || "",
    });
    setChefChantier((item.chefChantier || []).map(m => m._id || m));
    setTechniciens((item.techniciens || []).map(m => m._id || m));
    setSelectedNoeuds(item.noeuds || []);
    setSearchNoeud("");
    setFilterCategorie("");
  }, [item, isOpen]);

  // Charger revendeurs au montage
  useEffect(() => {
    if (!isOpen) return;
    if (!isRevendeur) {
      cascadeAPI.revendeurs().then(res => setRevendeurs(res.data.data || res.data || [])).catch(console.error);
    }
  }, [isOpen, isRevendeur]);

  // Charger membres quand revendeur change
  useEffect(() => {
    if (!isOpen) return;
    const params = {};
    if (user?.role === 'admin' && form.revendeur) params.revendeur = form.revendeur;
    membresAPI.getAll(params).then(res => {
      setMembres(res.data.data || res.data || []);
    }).catch(console.error);
  }, [isOpen, form.revendeur, user?.role]);

  // Charger clients quand revendeur change
  useEffect(() => {
    if (!form.revendeur) { setClients([]); return; }
    cascadeAPI.clients(form.revendeur).then(res => setClients(res.data.data || res.data || [])).catch(console.error);
  }, [form.revendeur]);

  // Charger projets quand client change
  useEffect(() => {
    if (!form.client) { setProjets([]); return; }
    cascadeAPI.projets(form.client, form.revendeur).then(res => {
      setProjets(res.data.data || res.data || []);
    }).catch(console.error);
  }, [form.client, form.revendeur]);

  // Charger nœuds disponibles en stock
  useEffect(() => {
    if (!isOpen) return;
    const params = {};
    if (filterCategorie) params.categorie = filterCategorie;
    if (searchNoeud) params.search = searchNoeud;
    noeudsAPI.getDisponibles(params).then(res => {
      const list = res.data?.data || res.data || [];
      setNoeudsDispos(list.map(n => ({ _id: n._id, sn: n.sn, type: n.type, categorie: n.categorie })));
    }).catch(console.error);
  }, [isOpen, filterCategorie, searchNoeud]);

  const handleChange = (field) => (e) => {
    const val = e.target.value;
    if (field === "revendeur") setForm(f => ({ ...f, revendeur: val, client: "", projet: "" }));
    else if (field === "client") setForm(f => ({ ...f, client: val, projet: "" }));
    else setForm(f => ({ ...f, [field]: val }));
  };

  const addNoeud = (n) => { if (!selectedNoeuds.find(x => x.sn === n.sn)) setSelectedNoeuds(p => [...p, { ...n, latitude: "", longitude: "" }]); };
  const removeNoeud = (sn) => setSelectedNoeuds(p => p.filter(x => x.sn !== sn));
  const updateNoeudGeo = (sn, field, val) => setSelectedNoeuds(p => p.map(n => n.sn === sn ? { ...n, [field]: val } : n));
  const autoLocate = (sn) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSelectedNoeuds(p => p.map(n => n.sn === sn ? { ...n, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) } : n));
      },
      () => {},
      { enableHighAccuracy: true }
    );
  };

  const filteredNoeuds = noeudsDispos.filter(n =>
    !selectedNoeuds.find(x => x.sn === n.sn)
  );

  const handleSubmit = () => {
    if (!form.nom || !form.client || !form.projet || !form.dateDebut || !form.dateFin) return;
    if (new Date(form.dateFin) < new Date(form.dateDebut)) return;
    const payload = { ...form, chefChantier, techniciens, noeuds: selectedNoeuds.map(n => ({ sn: n.sn, type: n.type, latitude: n.latitude ? parseFloat(n.latitude) : undefined, longitude: n.longitude ? parseFloat(n.longitude) : undefined })) };
    if (!payload.revendeur) payload.revendeur = null;
    onEdit?.(payload);
    onClose();
  };

  const selectStyle = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40";

  const categorieBadge = (cat) => {
    const colors = {
      Capteur: "bg-blue-100 text-blue-700",
      Actionneur: "bg-purple-100 text-purple-700",
      Passerelle: "bg-amber-100 text-amber-700",
    };
    return colors[cat] || "bg-gray-100 text-gray-700";
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifier le chantier" subtitle="Modifiez les informations du chantier sélectionné." size="2xl">
      <div className="flex flex-col md:flex-row gap-4 md:gap-6">

        {/* Colonne gauche */}
        <div className="flex-1 space-y-4">

          <FormField label="Nom du chantier" required>
            <Input value={form.nom} onChange={handleChange("nom")} placeholder="Ex: Installation capteurs - Bureau" />
          </FormField>

          <div className={`grid ${isRevendeur ? 'grid-cols-2' : 'grid-cols-3'} gap-3`}>
            {!isRevendeur && (
              <FormField label="Revendeur" required>
                <select value={form.revendeur} onChange={handleChange("revendeur")} className={selectStyle}>
                  <option value="">Sélectionner</option>
                  {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
                </select>
              </FormField>
            )}
            <FormField label="Client" required>
              <select value={form.client} onChange={handleChange("client")} disabled={!form.revendeur} className={selectStyle}>
                <option value="">Sélectionner</option>
                {clients.map(c => <option key={c._id} value={c._id}>{c.nom}</option>)}
              </select>
            </FormField>
            <FormField label="Projet" required>
              <select value={form.projet} onChange={handleChange("projet")} disabled={!form.client} className={selectStyle}>
                <option value="">Sélectionner</option>
                {projets.map(p => <option key={p._id} value={p._id}>{p.nom}</option>)}
              </select>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Date de début" required>
              <input type="date" value={form.dateDebut} onChange={handleChange("dateDebut")}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </FormField>
            <FormField label="Date de fin prévue" required>
              <input type="date" value={form.dateFin} onChange={handleChange("dateFin")}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </FormField>
          </div>

          <FormField label="État" required>
            <select value={form.etat} onChange={handleChange("etat")} className={selectStyle}>
              <option>En attente</option>
              <option>En cours</option>
              <option>En pause</option>
              <option>En attente de confirmation</option>
              {canDo('confirmerChantier') && <option>Terminé</option>}
            </select>
          </FormField>

          {/* Affectation équipe */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Chef(s) de chantier">
              <MemberMultiSelect
                membres={membres}
                selected={chefChantier}
                onChange={setChefChantier}
                defaultRole="Chef de Chantier"
                tagColor="bg-blue-50 text-blue-700"
              />
            </FormField>
            <FormField label="Technicien(s)">
              <MemberMultiSelect
                membres={membres}
                selected={techniciens}
                onChange={setTechniciens}
                defaultRole="Technicien IoT"
                tagColor="bg-purple-50 text-purple-700"
              />
            </FormField>
          </div>

          <FormField label="Commentaires">
            <textarea
              value={form.commentaires}
              onChange={handleChange("commentaires")}
              placeholder="Commentaires sur le chantier..."
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </FormField>
        </div>

        {/* Colonne droite — nœuds du stock */}
        <div className="w-full md:w-[480px] flex-shrink-0 flex flex-col">
          <div className="flex-1 border-2 border-dashed border-gray-200 rounded-2xl p-3 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Package size={14} className="text-slate-500" />
              <p className="text-xs font-semibold text-slate-700">Nœuds en stock</p>
            </div>

            {/* Filtre catégorie */}
            <div className="flex gap-1">
              {["", "Capteur", "Actionneur", "Passerelle"].map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilterCategorie(cat)}
                  className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                    filterCategorie === cat
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-slate-600 hover:bg-gray-200"
                  }`}
                >
                  {cat || "Tous"}
                </button>
              ))}
            </div>

            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={searchNoeud}
                onChange={e => setSearchNoeud(e.target.value)}
                placeholder="Rechercher SN ou type..."
                className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex-1 overflow-y-auto space-y-1">
              <p className="text-xs text-slate-400 font-medium mb-1">DISPONIBLES ({filteredNoeuds.length})</p>
              {filteredNoeuds.length === 0 && (
                <p className="text-xs text-slate-300 text-center py-4">Aucun nœud en stock</p>
              )}
              {filteredNoeuds.map(n => (
                <button key={n.sn} onClick={() => addNoeud(n)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white border border-gray-100 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all">
                  <div className="text-left">
                    <p className="text-xs font-semibold text-slate-800">{n.sn}</p>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium ${categorieBadge(n.categorie)}`}>{n.categorie}</span>
                      <span className="text-xs text-slate-400">{n.type}</span>
                    </div>
                  </div>
                  <Plus size={13} className="text-blue-500" />
                </button>
              ))}
            </div>

            {selectedNoeuds.length > 0 && (
              <div>
                <p className="text-xs text-slate-400 font-medium mb-1">ASSIGNÉS ({selectedNoeuds.length})</p>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {selectedNoeuds.map(n => (
                    <div key={n.sn} className="px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl">
                      <div className="flex items-center justify-between mb-1.5">
                        <div>
                          <p className="text-xs font-semibold text-slate-800">{n.sn}</p>
                          <p className="text-[10px] text-slate-400">{n.type}</p>
                        </div>
                        <button onClick={() => removeNoeud(n.sn)} className="p-1 hover:bg-red-100 rounded-lg">
                          <X size={12} className="text-red-400" />
                        </button>
                      </div>
                      <div className="grid grid-cols-[1fr_1fr_auto] gap-1.5 items-center">
                        <input
                          type="number" step="any" placeholder="Latitude"
                          value={n.latitude || ""}
                          onChange={e => updateNoeudGeo(n.sn, "latitude", e.target.value)}
                          className="w-full px-2 py-1 border border-blue-200 rounded-lg text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 min-w-0"
                        />
                        <input
                          type="number" step="any" placeholder="Longitude"
                          value={n.longitude || ""}
                          onChange={e => updateNoeudGeo(n.sn, "longitude", e.target.value)}
                          className="w-full px-2 py-1 border border-blue-200 rounded-lg text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 min-w-0"
                        />
                        <button
                          onClick={() => autoLocate(n.sn)}
                          title="Utiliser ma position"
                          className="px-2 py-1 bg-blue-600 text-white rounded-lg text-[10px] font-semibold hover:bg-blue-700 flex items-center gap-1 whitespace-nowrap"
                        >
                          <LocateFixed size={10} /> Auto
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Annuler</Button>
        <Button onClick={handleSubmit}>Sauvegarder</Button>
      </div>
    </Modal>
  );
}
